import * as closedByPolyfill from './dialog-closedby'

/**
 * Adapted from https://github.com/nuxodin/dialog-polyfill, which is not published on npm
 *
 * This should no longer be required once support for Safari v14 is dropped.
 */
export function apply() {
  if (typeof window !== 'undefined' && !window.HTMLDialogElement) {
    let activeDialog: HTMLDialogElement | null = null
    let maxZIndex = 1000

    window.HTMLDialogElement =
      class HTMLDialogElement extends HTMLUnknownElement {
        #lastActiveElement: HTMLElement | null = null
        #backdrop: HTMLDivElement | null = null

        constructor() {
          super()
          this.setAttribute('role', 'dialog')
        }

        showModal() {
          this.#lastActiveElement = document.activeElement as HTMLElement

          this.style.display = 'block'
          setTimeout(() => {
            // makes backdrop-transitions work
            this.style.display = ''
            this.show()
            this.setAttribute('aria-modal', 'true')
          })
          this.classList.add('dialog-polyfill-as-modal')

          // focus first element autofocus if available
          const focusableEl =
            this.querySelector<HTMLElement>('[autofocus]') ||
            this.querySelector<HTMLElement>(
              'a[href],button,input,textarea,select,details,[contenteditable],[tabindex]',
            )
          focusableEl?.focus()

          this.addEventListener('blur', preventBlurListener, true)
          this.addEventListener('keydown', escListener, true)

          // backdrop
          if (!this.#backdrop) {
            this.#backdrop = document.createElement('div')
            this.#backdrop.classList.add('backdrop')
          }
          this.#backdrop.style.zIndex = String(maxZIndex++)
          this.after(this.#backdrop)

          this.style.zIndex = String(maxZIndex++)

          // eslint-disable-next-line @typescript-eslint/no-this-alias
          activeDialog = this as typeof window.HTMLDialogElement
        }

        show() {
          this.setAttribute('open', '')
        }

        close(returnValue?: string) {
          this.classList.remove('dialog-polyfill-as-modal')
          this.removeAttribute('open')
          this.setAttribute('aria-modal', 'false')
          this.#backdrop?.remove()
          this.removeEventListener('blur', preventBlurListener, true)
          this.removeEventListener('keydown', escListener, true)
          if (returnValue != null && activeDialog) {
            activeDialog.returnValue = returnValue
          }
          activeDialog = null
          ;(this.#lastActiveElement as HTMLElement)?.focus()
          const event = new Event('close', { bubbles: false })
          this.dispatchEvent(event)
        }

        get open() {
          return this.hasAttribute('open')
        }

        set open(isOpen: boolean) {
          if (isOpen) this.show()
          else this.close()
        }
      } as typeof window.HTMLDialogElement

    document.addEventListener(
      'submit',
      (e) => {
        if ((e.target as HTMLElement)?.getAttribute('method') !== 'dialog')
          return
        e.preventDefault()
        activeDialog?.close((e.submitter as HTMLButtonElement)?.value)
      },
      true,
    )

    const css =
      'dialog{' +
      'display:block;' +
      'position:absolute;' +
      'left:0;' +
      'right:0;' +
      'width:fit-content;' +
      'height:fit-content;' +
      'margin:auto;' +
      'border-width:initial;' +
      'border-style:solid;' +
      'border-color:initial;' +
      'border-image:initial;' +
      'padding:1em;' +
      'background:white;' +
      'background:Canvas;' +
      'color:black;' +
      'color:CanvasText;' +
      '}' +
      'dialog:not([open]){' +
      'display:none;' +
      '}' +
      '.dialog-polyfill-as-modal{' +
      'position:fixed;' +
      'top:0;' +
      'bottom:0;' +
      'max-width:calc((100% - 6px) - 2em);' +
      'max-height:calc((100% - 6px) - 2em);' +
      'overflow:auto;' +
      '}' +
      'dialog + .backdrop{' +
      'position:fixed;' +
      'top:0;' +
      'right:0;' +
      'bottom:0;' +
      'left:0;' +
      'background:#0002;' +
      '}'
    document.head.insertAdjacentHTML('afterbegin', '<style>' + css + '</style>')

    function preventBlurListener(e: FocusEvent) {
      if (!e.relatedTarget) return
      if (!activeDialog?.contains(e.relatedTarget as HTMLElement)) {
        setTimeout(() => {
          ;(e.target as HTMLElement)?.focus()
        })
      }
    }

    function escListener(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        const event = new Event('cancel', { bubbles: true, cancelable: true })
        activeDialog?.dispatchEvent(event)
        if (!event.defaultPrevented) {
          activeDialog?.close()
        }
      }
    }
  }

  closedByPolyfill.apply()
}
